import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { urlify } from 'utils/index';
import { DEFAULT_SIGNUP_PLAN, } from 'constants/index';
import { handleServerError } from 'shared-services/error-service';
import { setUserTrackingProp, dataLayerPush } from 'services/events-tracker-service';
import { getPasswordErrors, validateEmail, validateUrl } from 'utils/validators';
import { signup, createTeam } from 'services/onboarding-service';

import { MeetingEmbedContext } from 'context/MeetingEmbedContext';

import FieldDefault from 'components/field-default';
import PasswordErrors from 'components/PasswordErrors';

import './QuickSignup.styles.css';

const QuickSignup = ({ onBack }) => {
    const { t } = useTranslation(['onboarding', 'apps', 'common', 'user', 'team']);
    const meetingEmbedCtx = useContext(MeetingEmbedContext);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [teamName, setTeamName] = useState('');
    const [teamUrl, setTeamUrl] = useState('');
    const [webexToken, setWebexToken] = useState(undefined);
    const [zoomUserId, setZoomUserId] = useState(undefined);

    const plan = useMemo(() => DEFAULT_SIGNUP_PLAN, []);

    const cleanPlan = useMemo(() => {
        if (!plan) {
            return undefined;
        }

        return plan.replace(/_([a-z]|[A-Z])+$/, '');
    }, [plan]);

    const passwordErrors = useMemo(() => {
        return getPasswordErrors(password, undefined, email);
    }, [password, email]);

    const populated = useMemo(() => {
        return name && validateEmail(email) && passwordErrors.length === 0 && teamName && teamUrl;
    }, [email, name, password, teamName, teamUrl]);

    const handleEditClick = () => {
        setEditing(true);
    }

    const createUserRequest = useCallback(() => {
        return signup({
            email,
            name,
            password,
            webex_token: webexToken,
            zoom_user_id: zoomUserId
        });
    }, [email, name, password, webexToken, zoomUserId]);

    const createTeamRequest = useCallback(() => {
        return createTeam({
            url: teamUrl,
            name: teamName,
            plan,
            signup: true
        });
    }, [teamUrl, teamName, plan]);

    const handleSignup = useCallback(async () => {
        if (loading) {
            return;
        }

        setLoading(true);

        try {
            await createUserRequest();

            dataLayerPush('signup_profile', {
                plan_id: cleanPlan
            });

            await createTeamRequest();

            setUserTrackingProp('webex', true);

            dataLayerPush('signup_team', {
                plan_id: cleanPlan
            });
        } catch(e) {
            handleServerError(e, {}, false);
        }

        setLoading(false);
    }, [loading, createUserRequest, createTeamRequest, cleanPlan]);


    const handleTeamNameChange = useCallback(value => {
        setTeamName(value);
        setTeamUrl(urlify(value));
    }, [setName, setTeamUrl]);

    const handleTeamUrlBlur = useCallback(value => {
        setTeamUrl(urlify(value));
    }, [setTeamUrl]);

    const init = useCallback(async () => {
        if (meetingEmbedCtx?.inApp) {
            let user = {};
            let space = {};

            try {
                user = meetingEmbedCtx.user;
                // user = await meetingEmbedCtx.app.context.getUser();

                setEmail(user?.email);
                setName(user?.displayName);
                if (meetingEmbedCtx.provider === 'webex') {
                    setWebexToken(user?.token);
                } else {
                    setZoomUserId(user?.id);
                }
            } catch (e) {
                console.error(e);
            }

            try {
                if (meetingEmbedCtx.provider === 'webex') {
                    space = await meetingEmbedCtx.app.context.getSpace();
                    setTeamName(space.title);
                    setTeamUrl(urlify(space.title));
                }
            } catch (e) {
                console.error(e);
            }

            if (!user.email || !user.displayName || !space.title || !urlify(space.title)) {
                setEditing(true);
            }
        }
    }, []);

    useEffect(() => {
        init();
    }, [init]);

    return (
        <div className="quick-signup">
            <div className="form onboarding-form onboarding-email">
                {
                    !editing ? (
                        <React.Fragment>
                            <div className="quick-signup-section">
                                <h5>{ t('Signup Info') }</h5>
                            </div>

                            <div className="quick-signup-section">
                                <span className="quick-signup-label">{ t('common:Email') }:</span> <strong>{ email }</strong><br />
                                <span className="quick-signup-label">{ t('common:Name') }:</span> <strong>{ name }</strong>
                            </div>

                            <div className="quick-signup-section">
                                <span className="quick-signup-label">{ t('team:workspaceName') }:</span> <strong>{ teamName }</strong><br />
                                <span className="quick-signup-label">{ t('team:workspaceURL') }:</span> <strong>{ teamUrl }</strong>
                            </div>

                            <div className="quick-signup-section">
                                <button className="btn btn-secondary" onClick={handleEditClick}>{ t('common:Edit') }</button>
                            </div>
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <div className="form-row">
                                <FieldDefault
                                    id="signup-email"
                                    type="email"
                                    label={ t('common:Email Address') }
                                    placeholder={ t('common:Enter email') }
                                    value={email}
                                    onChange={setEmail}
                                />
                            </div>

                            <div className="form-row">
                                <FieldDefault
                                    id="signup-fullname"
                                    label={ t('common:Full Name') }
                                    placeholder={ t('user:settings.fullNameDescription') }
                                    value={name}
                                    onChange={setName}
                                />
                            </div>

                            <div className="form-row">
                                <FieldDefault
                                    id="signup-teamname"
                                    label={ t('team:workspaceName') }
                                    placeholder={ t('team:newTeamNamePlaceholder') }
                                    value={teamName}
                                    onChange={handleTeamNameChange}
                                />
                            </div>

                            <div className="form-row">
                                <FieldDefault
                                    id="signup-teamurl"
                                    label={ t('team:workspaceURL') }
                                    placeholder="workspace"
                                    value={teamUrl}
                                    onChange={setTeamUrl}
                                    onBlur={handleTeamUrlBlur}
                                    addon=".nifty.pm"
                                />
                            </div>
                        </React.Fragment>
                    )
                }

                <div className="form-row">
                    <FieldDefault
                        id="signup-password"
                        type="password"
                        label={ t('user:password.Create Password') }
                        placeholder={ t('user:password.passwordRequirement') }
                        value={password}
                        onChange={setPassword}
                    />

                    {
                        password !== '' ? (
                            <PasswordErrors value={passwordErrors} />
                        ) : null
                    }
                </div>

                <div className="form-row">
                    <div className="btn-group btn-group-auto">
                        <button className="btn btn-secondary" onClick={onBack}>Back</button>

                        <button className={'btn btn-success btn-block btn-has-loading' + (loading ? ' loading' : '')} disabled={!populated} onClick={handleSignup}>Complete Signup</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuickSignup;
